import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowDown, ArrowUp } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import MainCard from '../../components/MainCard';
import { IFeeTypeTotal } from '../../apis/models/transactions';
import { zipPeriodTotalsByFeeType } from './stats-report';

interface PeriodComparisonTableProps {
  title: string;
  periodATotals: IFeeTypeTotal[];
  periodBTotals: IFeeTypeTotal[];
  resolveFeeType: (enumKey: string) => string;
  currency: string;
  labelA: string;
  labelB: string;
  /** Expenses read better inverted — a spend increase is the "bad" direction. */
  invertColor?: boolean;
}

const fmt = (currency: string, amount: number) => `${currency} ${amount.toFixed(2)}`;

const PeriodComparisonTable: React.FC<PeriodComparisonTableProps> = ({
  title,
  periodATotals,
  periodBTotals,
  resolveFeeType,
  currency,
  labelA,
  labelB,
  invertColor = false,
}) => {
  const { t } = useTranslation();

  const rows = useMemo(
    () => zipPeriodTotalsByFeeType(periodATotals, periodBTotals),
    [periodATotals, periodBTotals]
  );

  if (rows.length === 0) return null;

  return (
    <MainCard title={title} sx={{ mb: 2 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('tables.fee_type')}</TableCell>
              <TableCell align="right">{labelA}</TableCell>
              <TableCell align="right">{labelB}</TableCell>
              <TableCell align="right">Δ</TableCell>
              <TableCell align="right">Δ%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const isGood = invertColor ? !row.delta.isPositive : row.delta.isPositive;
              return (
                <TableRow key={row.key}>
                  <TableCell>{resolveFeeType(row.key)}</TableCell>
                  <TableCell align="right">{fmt(currency, row.totalA)}</TableCell>
                  <TableCell align="right">{fmt(currency, row.totalB)}</TableCell>
                  <TableCell align="right" sx={{ color: isGood ? 'success.main' : 'error.main' }}>
                    {row.delta.isPositive ? '+' : ''}
                    {fmt(currency, row.delta.delta)}
                  </TableCell>
                  <TableCell align="right">
                    {row.delta.deltaPercentage === null ? (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <Box display="flex" justifyContent="flex-end">
                        <Chip
                          variant="combined"
                          color={isGood ? 'success' : 'error'}
                          icon={
                            row.delta.isPositive ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                          }
                          label={`${Math.abs(row.delta.deltaPercentage)}%`}
                          size="small"
                          sx={{ pl: 1, borderRadius: 1 }}
                        />
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </MainCard>
  );
};

export default PeriodComparisonTable;
